import { useEffect, useState } from 'react';
import { Bot, CheckCircle2, KeyRound, Save, Settings, ShieldCheck } from 'lucide-react';
import { get, put } from '../lib/api';

export function SettingsPage() {
  const [data, setData] = useState<any>(null);
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState({ current_password: '', new_password: '', repeat: '' });

  useEffect(() => { get<any>('/api/settings').then(setData); }, []);
  if (!data) return <div className="skeleton-page" />;

  const save = async () => {
    try {
      const payload = {
        organization_name: data.organization_name,
        report_period_label: data.report_period_label,
        max_auto_create_incidents: data.max_auto_create_incidents,
        max_classifier_threshold: data.max_classifier_threshold,
        max_chat_id: data.max_chat_id,
        max_classifier_keywords: typeof data.max_classifier_keywords === 'string'
          ? data.max_classifier_keywords
          : JSON.stringify(data.max_classifier_keywords)
      };
      const result = await put<any>('/api/settings', payload);
      setData({ ...data, ...result });
      setMessage('Настройки сохранены');
    } catch (error: any) { setMessage(error.message); }
  };

  const changePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password.new_password !== password.repeat) {
      setMessage('Новые пароли не совпадают');
      return;
    }
    try {
      await put('/api/auth/password', {
        current_password: password.current_password,
        new_password: password.new_password
      });
      setPassword({ current_password: '', new_password: '', repeat: '' });
      setMessage('Пароль администратора изменён');
    } catch (error: any) { setMessage(error.message); }
  };

  return <>
    <section className="section-intro">
      <div>
        <div className="eyebrow">Конфигурация</div>
        <h2>Настройки системы</h2>
        <p>Секреты и токены задаются только в серверном файле <code>/etc/dvvs-monitoring.env</code>.</p>
      </div>
      <button className="primary" onClick={() => void save()}><Save size={18} />Сохранить</button>
    </section>
    {message && <div className="save-message">{message}</div>}
    <div className="settings-grid">
      <article className="panel">
        <div className="panel-head"><div><div className="eyebrow">Общие</div><h3>Организация и отчёт</h3></div><Settings /></div>
        <label>Название организации<input value={data.organization_name || ''} onChange={e => setData({ ...data, organization_name: e.target.value })} /></label>
        <label>Название отчётного периода<input value={data.report_period_label || ''} onChange={e => setData({ ...data, report_period_label: e.target.value })} /></label>
      </article>

      <article className="panel">
        <div className="panel-head"><div><div className="eyebrow">Доступ</div><h3>Пароль администратора</h3></div><ShieldCheck /></div>
        <form onSubmit={changePassword}>
          <label>Текущий пароль<input type="password" autoComplete="current-password" value={password.current_password} onChange={e => setPassword({ ...password, current_password: e.target.value })} /></label>
          <label>Новый пароль<input type="password" minLength={12} autoComplete="new-password" value={password.new_password} onChange={e => setPassword({ ...password, new_password: e.target.value })} /></label>
          <label>Повторите новый пароль<input type="password" minLength={12} autoComplete="new-password" value={password.repeat} onChange={e => setPassword({ ...password, repeat: e.target.value })} /></label>
          <button className="secondary" type="submit"><KeyRound size={16} />Изменить пароль</button>
        </form>
      </article>

      <article className="panel">
        <div className="panel-head"><div><div className="eyebrow">MAX</div><h3>Подключение бота</h3></div><Bot /></div>
        <div className="secret-status">
          <SecretStatus ok={data.max_token_configured} label="MAX_BOT_TOKEN" />
          <SecretStatus ok={data.max_webhook_secret_configured} label="MAX_WEBHOOK_SECRET" />
          <SecretStatus ok={data.max_relay_secret_configured} label="MAX_RELAY_SECRET" />
        </div>
        <label>Chat ID технической группы<input value={data.max_chat_id || ''} onChange={e => setData({ ...data, max_chat_id: e.target.value })} /></label>
        <div className="endpoint-box"><KeyRound /><div><span>Webhook endpoint</span><code>https://monitoring.dvvs-ekb.ru/api/integrations/max/webhook</code></div></div>
        <div className="endpoint-box"><KeyRound /><div><span>Relay endpoint существующего MAX-шлюза</span><code>https://monitoring.dvvs-ekb.ru/api/integrations/max/relay</code></div></div>
      </article>

      <article className="panel">
        <div className="panel-head"><div><div className="eyebrow">Автоматизация</div><h3>Режим обработки MAX</h3></div></div>
        <label>Порог уверенности, от 0 до 1<input type="number" min="0" max="1" step="0.05" value={data.max_classifier_threshold || '0.55'} onChange={e => setData({ ...data, max_classifier_threshold: e.target.value })} /></label>
        <label className="switch-row"><span><strong>Автоматически создавать инциденты</strong><small>По умолчанию отключено. Безопаснее сначала проверять очередь вручную.</small></span><input type="checkbox" checked={String(data.max_auto_create_incidents) === 'true'} onChange={e => setData({ ...data, max_auto_create_incidents: String(e.target.checked) })} /></label>
      </article>

      <article className="panel span-2">
        <div className="panel-head"><div><div className="eyebrow">Правила классификации</div><h3>Ключевые слова ИТ-заявок</h3></div></div>
        <p className="muted">JSON-массив фраз. Совпадения с системой и признаки неисправности повышают оценку сообщения.</p>
        <textarea rows={14} value={data.max_classifier_keywords || '[]'} onChange={e => setData({ ...data, max_classifier_keywords: e.target.value })} />
      </article>
    </div>
  </>;
}

function SecretStatus({ ok, label }: { ok: boolean; label: string }) {
  return <div className={ok ? 'ok' : 'warn'}>{ok ? <CheckCircle2 /> : <KeyRound />}<span>{label}</span><strong>{ok ? 'задан' : 'не задан'}</strong></div>;
}
